import React, { useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PublicRegistrationFlowProvider } from './contexts/PublicRegistrationFlowContext';
import { TabProvider, useTabContextSafe } from './contexts/TabContext';
import { ROUTE_COMPONENT_MAP, getRouteTitle, getCanonicalTabPath } from './components/layout/routeComponentMap';
import { ThemeProvider } from './contexts/ThemeContext';
import CssBaseline from '@mui/material/CssBaseline';
import { HelmetProvider } from 'react-helmet-async';
import { TableCartProvider } from './components/salesSystem/restaurant/TableCartContext'; // Import TableCartProvider
import Login from './components/Login';
import ResetPassword from './components/ResetPassword';
import Register from './components/Register';
import RegisterSelection from './components/RegisterSelection';
import PersonnelRegister from './components/PersonnelRegister';
import Welcome from './components/welcome/Welcome';
import Features from './components/welcome/Features';
import Contact from './components/welcome/Contact';
import PreAccounting from './components/welcome/PreAccounting';
import PrivacyPolicy from './components/welcome/PrivacyPolicy';
import TermsOfService from './components/welcome/TermsOfService';
import GorevTakibi from './components/welcome/GorevTakibi';
import PersonelYonetimi from './components/welcome/PersonelYonetimi';
import VardiyaTakibi from './components/welcome/VardiyaTakibi';
import Raporlar from './components/welcome/Raporlar';
import Bildirimler from './components/welcome/Bildirimler';
import AnketYonetimi from './components/welcome/AnketYonetimi';
import EtkinlikYonetimi from './components/welcome/EtkinlikYonetimi';
import SubeYonetimi from './components/welcome/SubeYonetimi';
import Ecommerce from './components/welcome/Ecommerce';
import QRMenu from './components/welcome/QRMenu';
import Kiosk from './components/welcome/Kiosk';
import SalesSystems from './components/welcome/SalesSystems';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import './App.css';
// React Grid Layout CSS'leri
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import MainLayout from './components/layout/MainLayout';
import ManagerLayout from './components/manager/ManagerLayout';
import Dashboard from './components/dashboard/Dashboard';
import Tasks from './components/tasks/Tasks';
import Personnel from './components/personnel/Personnel';
import PersonnelActions from './components/personnel/PersonnelActions';
import UsersPage from './components/users/UsersPage';
import {
  isPathAllowedForSubAdmin,
  getFirstAllowedPathForSubAdmin,
} from './components/users/adminModuleMap';
import Profile from './components/profile/Profile';
import AiUsagePage from './components/aichat/usage/AiUsagePage';
import Reports from './components/reports/Reports';
import EndOfDayReport from './components/reports/EndOfDayReport';
import EndOfDayReportsList from './components/reports/EndOfDayReportsList';
import Messages from './components/messages/Messages';
import Surveys from './pages/Surveys';
import PublicSurvey from './pages/PublicSurvey';
import ShiftsPage from './pages/Shifts';
import BranchesPage from './pages/Branches';
import CustomerScreenPage from './pages/CustomerScreen';

// İnsan Kaynakları - CV Başvuru Formu
import CVApplicationForm from './pages/HumanResources/CVApplicationForm';

import ManagerPage from './pages/ManagerDashboard';
import ManagerProfilePage from './pages/ManagerProfile';
import SuperAdmin from './components/superadmin/SuperAdmin';
import SuperAdminPersonnel from './components/superadmin/SuperAdminPersonnel';
import SuperAdminContacts from './components/superadmin/SuperAdminContacts';
import DeletionRequests from './components/superadmin/DeletionRequests';
import SuperAdminUserMigration from './components/superadmin/SuperAdminUserMigration';
import SuperAdminPermissionMigration from './components/superadmin/SuperAdminPermissionMigration';
import SuperAdminTaskMigration from './components/superadmin/SuperAdminTaskMigration';
import MiddlewareMonitor from './components/superadmin/MiddlewareMonitor';
import SuperAdminMailReports from './components/superadmin/mailReports/SuperAdminMailReports';
import SuperAdminDealers from './components/superadmin/SuperAdminDealers';
import ImpersonationFab from './components/ImpersonationFab';
import Reservations from './components/reservations/Reservations';
import BranchReservationSchedule from './components/reservations/components/BranchReservationSchedule';
import BranchReservationRequests from './components/reservations/components/BranchReservationRequests';
import Events from './components/events/Events';
import Accounts from './components/accounting/accounts/Accounts';
import Inventory from './components/accounting/inventory/Inventory';
import ImageLibrary from './components/accounting/inventory/ImageLibrary';
import BranchPrices from './components/accounting/branchPrices/BranchPrices';
import Bank from './components/accounting/bank/Bank';
import CreditCards from './components/accounting/bank/CreditCards';
import Loans from './components/accounting/bank/loans/Loans';
import LoanPayments from './components/accounting/bank/loans/LoanPayments';
import CheckBooks from './components/accounting/bank/CheckBooks';
import CheckBookLeaves from './components/accounting/bank/CheckBookLeaves';
import Cashbox from './components/accounting/cashbox/Cashbox';
import CashboxTransactions from './components/accounting/cashbox/CashboxTransactions';
import Warehouse from './components/accounting/warehouse/Warehouse';
import Invoice from './components/accounting/invoice/Invoice';
import SalesInvoice from './components/accounting/invoice/SalesInvoice';
import InvoiceReports from './components/accounting/invoice/InvoiceReports';
import Offers from './components/accounting/offers/Offers';
import OfferTemplateEditor from './components/accounting/offers/OfferTemplateEditor';
import Waybills from './components/accounting/waybills/Waybills';
import Orders from './components/accounting/orders/Orders';
import ChecksNotes from './components/accounting/checksNotes/ChecksNotes';
import Projects from './components/accounting/projects/Projects';
import ProjectDetail from './components/accounting/projects/ProjectDetail';
import Currency from './components/accounting/currency/Currency';
import StockMovementsReport from './components/accounting/inventory/StockMovementsReport';
import PriceChangeLogs from './components/accounting/inventory/PriceChangeLogs';
import PurchaseInvoice from './components/accounting/invoice/PurchaseInvoice';
import AccountMovementsReport from './components/accounting/accounts/AccountMovementsReport';
import AccountTransactions from './components/accounting/accounts/AccountTransactions';
import BankTransactionReport from './components/accounting/bank/BankTransactionReport';
import AccountingDashboard from './components/accounting/dashboard/Dashboard';
import MailReports from './components/accounting/mailReports/MailReports';
import PersonnelTaskBoard from './components/employeePortal/PersonnelTaskBoard';

// İnsan Kaynakları Sayfaları
import LeaveManagementPage from './components/human-resources/leave-management/LeaveManagementPage';
import PerformanceManagementPage from './components/human-resources/performance-management/PerformanceManagementPage';
import TrainingDevelopmentPage from './components/human-resources/training-development/TrainingDevelopmentPage';
import PayrollPaymentsPage from './components/human-resources/payroll-payments/PayrollPaymentsPage';
import DocumentManagementPage from './components/human-resources/document-management/DocumentManagementPage';
import RecruitmentManagementPage from './components/human-resources/recruitment-management/RecruitmentManagementPage';

// Satış Sistemi Sayfaları
import QuickSalePage from './components/salesSystem/quickSale/QuickSalePage';
import RestaurantPage from './components/salesSystem/restaurant/RestaurantPage';
import OrderManagementPage from './components/salesSystem/orderManagement/OrderManagementPage'; // ✅ YENİ: Sipariş Yönetim Sistemi
import PackageOrderScreen from './components/salesSystem/orderManagement/PackageOrderScreen'; // ✅ YENİ: Paket Sipariş Ekranı
import AllOrdersPage from './components/salesSystem/orderManagement/AllOrdersPage';
import ClubEntryPage from './components/salesSystem/clubEntry/ClubEntryPage';
import QRMenuPage from './components/qr-menu/QRMenuPage';
import CustomersPage from './components/qr-menu/components/CustomersPage';
import OrdersPage from './components/qr-menu/components/OrdersPage';
import TablesPage from './components/qr-menu/components/TablesPage';
import QRMenuCustomerView from './components/qr-menu/customer/QRMenuCustomerView';
import PublicReservationForm from './components/reservations/components/PublicReservationForm';
import QuickSaleScreen from './components/salesSystem/quickSale/QuickSaleScreen';
import CustomerDisplayScreen from './components/salesSystem/quickSale/CustomerDisplayScreen';
import PykScreenView from './components/salesSystem/quickSale/PykScreenView';
import KitchenScreen from './components/kitchen/KitchenScreen';
import KitchenMenuSelection from './components/kitchen/KitchenMenuSelection';
import PoleDisplayScreen from './components/pole-display/PoleDisplayScreen';
import PoleDisplayMenuSelection from './components/pole-display/PoleDisplayMenuSelection';
import PoleDisplayManagement from './components/pole-display/PoleDisplayManagement';
import SalesScreenReportsPage from './components/salesSystem/quickSale/components/SalesScreenReportsPage';
import RestaurantScreen from './components/salesSystem/restaurant/RestaurantScreen';
import TableOrderScreenPlaceholder from './components/salesSystem/restaurant/TableOrderScreenPlaceholder';
import RestaurantOrdersReport from './components/salesSystem/restaurant/components/RestaurantOrdersReport';
import ECommercePage from './components/e-commerce/ECommercePage';
import IntegrationPage from './components/integrations/IntegrationPage';
import HaciogluProductMapping from './components/e-commerce/components/HaciogluProductMapping';
import CourierManagement from './components/couriers/CourierManagement';
import KioskMenuPage from './components/kiosk/KioskMenuPage';
import KioskProducts from './components/kiosk/KioskProducts';
import EInvoice from './components/accounting/e-invoice/E-Invoice';
import PavoPayIntegrations from './components/accounting/e-invoice/PavoPayIntegrations';
import DeviceManagement from './components/device-management/DeviceManagement';
import Cards from './components/cardManagement/Cards';
import CardSessions from './components/cardManagement/CardSessions';
import DeletedCards from './components/cardManagement/DeletedCards';
import QuickStartPage from './components/quick-start/QuickStartPage';

// WMS Sayfaları
import WmsDashboard from './components/wms/pages/WmsDashboard';
import WmsWarehouseSetup from './components/wms/pages/WmsWarehouseSetup';
import WmsLocationManager from './components/wms/pages/WmsLocationManager';
import WmsOrderPool from './components/wms/pages/WmsOrderPool';
import WmsCancelManagement from './components/wms/pages/WmsCancelManagement';
import WmsWaveManagement from './components/wms/pages/WmsWaveManagement';
import WmsReceivingManagement from './components/wms/pages/WmsReceivingManagement';
import WmsCycleCountManagement from './components/wms/pages/WmsCycleCountManagement';
import WmsEquipmentManagement from './components/wms/pages/WmsEquipmentManagement';
import WmsPickingSetup from './components/wms/pages/WmsPickingSetup';
import WmsPackingSetup from './components/wms/pages/WmsPackingSetup';
import WmsReceivingSetup from './components/wms/pages/WmsReceivingSetup';
import WmsCycleCountSetup from './components/wms/pages/WmsCycleCountSetup';
import WmsReports from './components/wms/pages/WmsReports';
// WMS Tam Ekran
import WmsPickingScreen from './components/wms/screens/WmsPickingScreen';
import WmsPackingScreen from './components/wms/screens/WmsPackingScreen';
import WmsReceivingScreen from './components/wms/screens/WmsReceivingScreen';
import WmsStockCountScreen from './components/wms/screens/WmsStockCountScreen';
import WmsScreenChooser from './components/wms/pages/WmsScreenChooser';

// Henüz oluşturulmamış diğer sayfalar için geçici bileşenler
// const Reports = () => <div>Raporlar Sayfası</div>;
// Geçici Messages bileşeni yerine gerçek bileşeni kullanıyoruz

// Tema artık ThemeContext tarafından yönetiliyor
// Kullanıcı sidebar ayarlarından tema renklerini değiştirebilir
// Varsayılan değerler BRAND config'den alınır


// Özel PrivateRoute bileşeni
const PrivateRoute = () => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  
  // Auth durumu yüklenirken loading göster
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Giriş yapılmamışsa login sayfasına yönlendir
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Giriş yapılmışsa ilgili route'a erişim sağla
  return <Outlet />;
};

// Rol bazlı Route bileşeni
const RoleRoute = ({ requiredRole }: { requiredRole: string | string[] }) => {
  const { userDetails, loading } = useAuth();
  const location = useLocation();
  
  // Auth durumu yüklenirken loading göster
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Rolü kontrol et
  const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
  const hasAccess = userDetails?.role && allowedRoles.includes(userDetails.role);
  
  console.log('[RoleRoute] Rol kontrolü:', {
    userRole: userDetails?.role,
    requiredRole: requiredRole,
    allowedRoles: allowedRoles,
    path: location.pathname,
    hasAccess: hasAccess
  });
  
  if (!hasAccess) {
    // superadmin/dealer kendi paneline gönderilir. Login'e atılırsa state.from ile
    // deep-link → RoleRoute reddi → login döngüsü oluşuyordu (giriş butonu
    // "çalışmıyor" görünüyordu).
    if (userDetails?.role === 'superadmin' || userDetails?.role === 'dealer') {
      return <Navigate to="/superadmin" replace />;
    }
    // role == 'manager' ise manager sayfasına, role == 'admin' ise yönetici ekranına yönlendir.
    // NOT: admin fallback'i /dashboard (görev takip) DEĞİL /accounting/dashboard olmalı;
    // aksi halde yetkisiz rotaya düşen admin görev takip dashboardına atılıyor ve
    // logout→login (state.from) döngüsüyle her girişte oraya geri dönüyordu.
    if (userDetails?.role === 'manager') {
      return <Navigate to="/manager" replace />;
    } else if (userDetails?.role === 'admin') {
      return <Navigate to="/accounting/dashboard" replace />;
    } else if (userDetails?.role === 'subadmin') {
      // Alt admin: admin bloğuna gönder — SubAdminModuleGuard izinli ilk modüle yönlendirir.
      return <Navigate to="/dashboard" replace />;
    }
    // Employee veya diğer roller için login'e yönlendir
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Rol doğruysa içeriği göster
  return <Outlet />;
};

/**
 * Alt admin (subadmin) rota guard'ı — deny-by-default.
 * Admin bloğu rotalarını sarar: role==='subadmin' ise rota adminModuleMap
 * izin haritasıyla kontrol edilir; izinsiz rotada izinli ilk modüle yönlendirilir,
 * hiç izin yoksa bilgi ekranı gösterilir. admin rolü etkilenmez (pass-through).
 */
const SubAdminModuleGuard = () => {
  const { userDetails, adminPanelPermissions, adminPanelLoading, logout } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  if (userDetails?.role !== 'subadmin') {
    return <Outlet />;
  }

  // İzin haritası yüklenmeden karar verme (yanlış redirect'i önler).
  if (adminPanelLoading || adminPanelPermissions == null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isPathAllowedForSubAdmin(adminPanelPermissions, location.pathname)) {
    return <Outlet />;
  }

  const firstAllowed = getFirstAllowedPathForSubAdmin(adminPanelPermissions);
  if (firstAllowed && firstAllowed !== location.pathname) {
    return <Navigate to={firstAllowed} replace />;
  }

  // Hiç izinli modül yok → bilgi ekranı.
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: 2,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box component="h2" sx={{ m: 0 }}>{t('users.noPermissionTitle')}</Box>
      <Box sx={{ color: 'text.secondary', maxWidth: 480 }}>{t('users.noPermissionHint')}</Box>
      <Box
        component="button"
        onClick={() => logout()}
        sx={{
          mt: 1,
          px: 3,
          py: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          background: 'transparent',
          cursor: 'pointer',
          fontSize: 14,
        }}
      >
        {t('header.logout', { defaultValue: 'Çıkış Yap' })}
      </Box>
    </Box>
  );
};

/**
 * Panel (superadmin/bayi) rotaları için ortak guard.
 * RoleRoute'tan farkı: giriş yoksa login'e, rol uymuyorsa kullanıcının kendi
 * paneline yönlendirir. Tüm /superadmin rotaları bununla sarılır — daha önce
 * korumasız olan sayfalar (SuperAdminPersonnel, MiddlewareMonitor...) ve
 * sayfa içi kopyala-yapıştır rol kontrolleri tek noktadan çözülür.
 * UI koruması niteliğindedir; asıl yetki backend guard'larında (SuperAdminOnly/AdminScope).
 */
const RequireRole = ({ roles }: { roles: string[] }) => {
  const { currentUser, userDetails, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const role = (userDetails?.role || '').toLowerCase();
  if (!roles.includes(role)) {
    // Rol uymuyor: kullanıcıyı kendi ana sayfasına gönder (varlık sızdırmadan).
    if (role === 'dealer') return <Navigate to="/superadmin" replace />;
    if (role === 'admin') return <Navigate to="/accounting/dashboard" replace />;
    if (role === 'manager') return <Navigate to="/manager" replace />;
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
};

// Tab URL sync: URL degistiginde ilgili tab acilir, tab degistiginde URL guncellenir
const TabUrlSync: React.FC = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const tabContext = useTabContextSafe();
  const { userDetails, adminPanelPermissions } = useAuth();
  const isDesktop = useMediaQuery('(min-width:1000px)');
  const prevActiveTabIdRef = React.useRef<string | null>(null);
  // URL→tab senkronu sırasında tab→URL yönlendirmesini engelle (sekme ping-pong döngüsü)
  const syncingFromUrlRef = React.useRef(false);

  useEffect(() => {
    if (!tabContext || !isDesktop) return;
    // Aynı bileşeni gösteren rotalar tek sekmede toplanır (örn. /fatura/satis → /fatura/alis)
    const path = getCanonicalTabPath(location.pathname);
    if (!ROUTE_COMPONENT_MAP[path]) return;

    // Subadmin: login sonrası /dashboard yalnızca geçici uğraktır; SubAdminModuleGuard
    // izin haritası yüklenince izinli ilk modüle yönlendirir. İzinler yüklenmeden veya
    // izinsiz rotada sekme açma — aksi halde her girişte "Dashboard" (görev takip)
    // sekmesi otomatik açılıp kalıcılaşıyordu.
    if ((userDetails?.role || '').toLowerCase() === 'subadmin') {
      if (adminPanelPermissions == null) return;
      if (!isPathAllowedForSubAdmin(adminPanelPermissions, path)) return;
    }

    const localizedTitle = getRouteTitle(path, i18n.language) || ROUTE_COMPONENT_MAP[path].title;
    syncingFromUrlRef.current = true;
    tabContext.openTab(path, localizedTitle);
    // Aynı sekme yeniden seçilirse activeTabId effect tetiklenmez; bayrağı temizle
    const clearFlagTimer = window.setTimeout(() => {
      syncingFromUrlRef.current = false;
    }, 0);
    return () => window.clearTimeout(clearFlagTimer);
  }, [location.pathname, i18n.language, userDetails?.role, adminPanelPermissions]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!tabContext || !isDesktop) return;

    tabContext.tabs.forEach((tab) => {
      const localizedTitle = getRouteTitle(tab.path, i18n.language);
      if (localizedTitle && localizedTitle !== tab.title) {
        tabContext.updateTabTitle(tab.path, localizedTitle);
      }
    });
  }, [i18n.language, tabContext?.tabs, tabContext?.updateTabTitle, isDesktop]);

  useEffect(() => {
    if (!tabContext || !isDesktop) return;
    const currentActiveId = tabContext.activeTabId;
    if (prevActiveTabIdRef.current !== null && prevActiveTabIdRef.current !== currentActiveId) {
      if (syncingFromUrlRef.current) {
        // Bu aktif sekme değişimi URL navigasyonundan geldi; geri navigate etme
        syncingFromUrlRef.current = false;
      } else {
        const activeTab = tabContext.tabs.find(t => t.id === currentActiveId);
        if (activeTab && activeTab.path !== location.pathname) {
          navigate(activeTab.path, { replace: true });
        }
      }
    }
    prevActiveTabIdRef.current = currentActiveId;
  }, [tabContext?.activeTabId]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

// ✅ AuthProvider DIŞINDA render edilecek route'lar için wrapper
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* ══════════════════════════════════════════════════════════════════════
          MÜŞTERİ EKRANI - AuthProvider DIŞINDA
          Bu route Firebase Auth senkronizasyonunu tetiklememeli.
          Yeni pencere açıldığında ana penceredeki oturumu etkilememesi için
          AuthProvider dışında tanımlanıyor.
         ══════════════════════════════════════════════════════════════════════ */}
      <Route path="/customer-display/:screenId" element={<CustomerDisplayScreen />} />
      
      {/* Diğer tüm route'lar AuthProvider içinde */}
      <Route path="/*" element={<AuthenticatedApp />} />
    </Routes>
  );
};

// ✅ AuthProvider içinde render edilecek ana uygulama
const AuthenticatedApp: React.FC = () => {
  return (
    <AuthProvider>
      <PublicRegistrationFlowProvider>
      <TabProvider>
      <TableCartProvider>
        <TabUrlSync />
        <ImpersonationFab />
        <Routes>
          <Route path="/" element={<Welcome />} />
          <Route path="/features" element={<Features />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/sales-systems" element={<SalesSystems />} />
          <Route path="/pre-accounting" element={<PreAccounting />} />
          <Route path="/ecommerce" element={<Ecommerce />} />
          <Route path="/qr-menu" element={<QRMenu />} />
          <Route path="/kiosk" element={<Kiosk />} />

          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/register" element={<RegisterSelection />} />
          <Route path="/register/company" element={<Register />} />
          <Route path="/personnel-register" element={<PersonnelRegister />} />
          {/* Panel rotaları: superadmin + bayi (dealer) — veri backend'de scoped gelir */}
          <Route element={<RequireRole roles={['superadmin', 'dealer']} />}>
            <Route path="/superadmin" element={<SuperAdmin />} />
            <Route path="/superadmin/personnel" element={<SuperAdminPersonnel />} />
          </Route>
          {/* Yalnız süper admine özel panel rotaları */}
          <Route element={<RequireRole roles={['superadmin']} />}>
            <Route path="/superadmin/dealers" element={<SuperAdminDealers />} />
            <Route path="/superadmin/contacts" element={<SuperAdminContacts />} />
            <Route path="/superadmin/deletion-requests" element={<DeletionRequests />} />
            <Route path="/superadmin/user-migration" element={<SuperAdminUserMigration />} />
            <Route path="/superadmin/permission-migration" element={<SuperAdminPermissionMigration />} />
            <Route path="/superadmin/task-migration" element={<SuperAdminTaskMigration />} />
            <Route path="/superadmin/middleware-monitor" element={<MiddlewareMonitor />} />
            <Route path="/superadmin/mail-reports" element={<SuperAdminMailReports />} />
          </Route>
          
          {/* Özellik Sayfaları */}
          <Route path="/features/gorev-takibi" element={<GorevTakibi />} />
          <Route path="/features/personel-yonetimi" element={<PersonelYonetimi />} />
          <Route path="/features/vardiya-takibi" element={<VardiyaTakibi />} />
          <Route path="/features/raporlar" element={<Raporlar />} />
          <Route path="/features/bildirimler" element={<Bildirimler />} />
          <Route path="/features/anket-yonetimi" element={<AnketYonetimi />} />
          <Route path="/features/etkinlik-yonetimi" element={<EtkinlikYonetimi />} />
          <Route path="/features/sube-yonetimi" element={<SubeYonetimi />} />
          
          {/* Public Routes */}
          <Route path="/surveys/:taskId" element={<PublicSurvey />} />
          <Route path="/qr-menu/:menuId" element={<QRMenuCustomerView />} />
          <Route path="/ticket/:menuId" element={<QRMenuCustomerView />} />
          <Route path="/reservation/:companyId/:branchId" element={<PublicReservationForm />} />
          
          {/* CV Başvuru Formu (Layout olmadan - Public) */}
          <Route path="/hr/cv-application" element={<CVApplicationForm />} />
          
          {/* Hızlı Satış Ekranı (Layout olmadan) */}
          <Route path="/hizli-satis/ekran/:screenId" element={<QuickSaleScreen />} />
          
          {/* PYK Ekranı (Layout olmadan) */}
          <Route path="/pyk-ekrani/:screenId" element={<PykScreenView />} />
                
                {/* Mutfak Ekranı (Layout olmadan) */}
                <Route path="/mutfak-ekrani/:menuId" element={<KitchenScreen />} />
                
                {/* Müşteri Ekranı / Pole Display (Layout olmadan) */}
                <Route path="/musteri-ekrani/:menuId" element={<PoleDisplayScreen />} />
                
                {/* QuickSale rota altına yeni rota ekle */}
                <Route path="/hizli-satis/raporlar/:screenType/:screenId" element={<MainLayout><SalesScreenReportsPage /></MainLayout>} />
                
                {/* Restoran Ekranları (Tüm giriş yapmış kullanıcılar erişebilir - yetki kontrolü Firebase'de yapılıyor) */}
                <Route element={<PrivateRoute />}>
                  <Route path="/restaurant-screen/:setupId" element={<RestaurantScreen />} />
                  <Route path="restaurant-screen/:setupId/table/:tableId" element={<TableOrderScreenPlaceholder />} />
                </Route>
                
                {/* Paket Sipariş Ekranı (Tüm giriş yapmış kullanıcılar erişebilir - yetki kontrolü Firebase'de yapılıyor) */}
                <Route element={<PrivateRoute />}>
                  <Route path="/paket-ekrani/:screenId" element={<PackageOrderScreen />} />
                </Route>

                {/* WMS İşçi Ekranları (Hızlı Satış paterni: PrivateRoute, yetki kontrolü ekran içinde) */}
                <Route element={<PrivateRoute />}>
                  <Route path="/wms/secim" element={<WmsScreenChooser />} />
                  <Route path="/wms/toplama/:screenId" element={<WmsPickingScreen />} />
                  <Route path="/wms/paketleme/:screenId" element={<WmsPackingScreen />} />
                  <Route path="/wms/malkabul/:screenId" element={<WmsReceivingScreen />} />
                  <Route path="/wms/stoksayim/:screenId" element={<WmsStockCountScreen />} />
                </Route>
                
                {/* Admin Routes (admin + subadmin; subadmin rotaları SubAdminModuleGuard ile
                    modül izin haritasına göre deny-by-default filtrelenir) */}
                <Route element={<PrivateRoute />}>
                  {/* Kullanıcılar (çoklu admin yönetimi) — YALNIZ ana admin */}
                  <Route element={<RoleRoute requiredRole="admin" />}>
                    <Route path="/kullanicilar" element={<MainLayout><UsersPage /></MainLayout>} />
                  </Route>
                  <Route element={<RoleRoute requiredRole={["admin", "subadmin"]} />}>
                   <Route element={<SubAdminModuleGuard />}>
                    <Route path="/hizli-baslangic" element={<MainLayout><QuickStartPage /></MainLayout>} />
                    <Route path="/dashboard" element={<MainLayout><Dashboard /></MainLayout>} />
                    <Route path="/tasks" element={<MainLayout><Tasks /></MainLayout>} />
                    <Route path="/personnel" element={<MainLayout><Personnel /></MainLayout>} />
                    <Route path="/personnel/actions/:personnelId" element={<MainLayout><PersonnelActions /></MainLayout>} />
                    <Route path="/shifts" element={<MainLayout><ShiftsPage /></MainLayout>} />
                    <Route path="/reports" element={<MainLayout><Reports /></MainLayout>} />
                    <Route path="/messages" element={<MainLayout><Messages /></MainLayout>} />
                    <Route path="/surveys" element={<MainLayout><Surveys /></MainLayout>} />
                    <Route path="/events" element={<MainLayout><Events /></MainLayout>} />
                    <Route path="/reservations" element={<MainLayout><Reservations /></MainLayout>} />
<Route path="/reservations/branch/:branchId" element={<MainLayout><BranchReservationSchedule /></MainLayout>} />
<Route path="/reservations/branch/:branchId/requests" element={<MainLayout><BranchReservationRequests /></MainLayout>} />
                    <Route path="/branches" element={<MainLayout><BranchesPage /></MainLayout>} />
                    <Route path="/customer-screen" element={<MainLayout><CustomerScreenPage /></MainLayout>} />
                    <Route path="/profile" element={<MainLayout><Profile /></MainLayout>} />
                    <Route path="/ai-kullanim" element={<MainLayout><AiUsagePage /></MainLayout>} />
                    
                    {/* Ön Muhasebe Sayfaları */}
                    <Route path="/cari" element={<MainLayout><Accounts /></MainLayout>} />
                    <Route path="/cari-hareket-raporu" element={<MainLayout><AccountMovementsReport /></MainLayout>} />
                    <Route path="/cari/hareket/:accountId" element={<MainLayout><AccountTransactions /></MainLayout>} />
                    <Route path="/stok" element={<MainLayout><Inventory /></MainLayout>} />
                    <Route path="/sube-fiyatlari" element={<MainLayout><BranchPrices /></MainLayout>} />
                    <Route path="/resim-kutuphanesi" element={<MainLayout><ImageLibrary /></MainLayout>} />
                    <Route path="/stok-hareket-raporu" element={<MainLayout><StockMovementsReport /></MainLayout>} />
                    <Route path="/fiyat-degisim-loglari" element={<MainLayout><PriceChangeLogs /></MainLayout>} />
                    <Route path="/accounting/inventory/movements" element={<MainLayout><StockMovementsReport /></MainLayout>} />
                    <Route path="/banka" element={<MainLayout><Bank /></MainLayout>} />
                    <Route path="/kredi-kartlari" element={<MainLayout><CreditCards /></MainLayout>} />
                    <Route path="/krediler" element={<MainLayout><Loans /></MainLayout>} />
                    <Route path="/kredi-odemeleri" element={<MainLayout><LoanPayments /></MainLayout>} />
                    <Route path="/bank/transactions" element={<MainLayout><BankTransactionReport /></MainLayout>} />
                    <Route path="/kasa" element={<MainLayout><Cashbox /></MainLayout>} />
                    <Route path="/accounting/cashbox" element={<MainLayout><Cashbox /></MainLayout>} />
                    <Route path="/accounting/cashbox/transactions" element={<MainLayout><CashboxTransactions /></MainLayout>} />
                    <Route path="/accounting/dashboard" element={<MainLayout><AccountingDashboard /></MainLayout>} />
                    <Route path="/accounting/mail-raporlari" element={<MainLayout><MailReports /></MainLayout>} />
                    <Route path="/depo" element={<MainLayout><Warehouse /></MainLayout>} />
                    <Route path="/doviz" element={<MainLayout><Currency /></MainLayout>} />
                    <Route path="/fatura/alis" element={<MainLayout><Invoice /></MainLayout>} />
                    <Route path="/fatura/satis" element={<MainLayout><Invoice /></MainLayout>} />
                    <Route path="/fatura/satis/yeni" element={<MainLayout><SalesInvoice /></MainLayout>} />
                    <Route path="/fatura/alis/yeni" element={<MainLayout><PurchaseInvoice /></MainLayout>} />
                    <Route path="/fatura/satis/duzenle/:invoiceId" element={<MainLayout><SalesInvoice /></MainLayout>} />
                    <Route path="/fatura/alis/duzenle/:invoiceId" element={<MainLayout><PurchaseInvoice /></MainLayout>} />
                    <Route path="/fatura/raporlar" element={<MainLayout><InvoiceReports /></MainLayout>} />
                    <Route path="/e-fatura" element={<MainLayout><EInvoice /></MainLayout>} />
                    <Route path="/e-fatura/pavopay-entegrasyonlar" element={<MainLayout><PavoPayIntegrations /></MainLayout>} />
                    <Route path="/teklifler" element={<MainLayout><Offers /></MainLayout>} />
                    {/* ✅ SATIŞ TEKLİF MODU - SalesInvoice mode='offer' ile */}
                    <Route path="/teklifler/satis/yeni" element={<MainLayout><SalesInvoice mode="offer" /></MainLayout>} />
                    <Route path="/teklifler/satis/duzenle/:invoiceId" element={<MainLayout><SalesInvoice mode="offer" /></MainLayout>} />
                    {/* ✅ ALIŞ TEKLİF MODU - PurchaseInvoice mode='offer' ile */}
                    <Route path="/teklifler/alis/yeni" element={<MainLayout><PurchaseInvoice mode="offer" /></MainLayout>} />
                    <Route path="/teklifler/alis/duzenle/:invoiceId" element={<MainLayout><PurchaseInvoice mode="offer" /></MainLayout>} />
                    {/* ✅ Eski route'lar (geriye dönük uyumluluk için) — liste artik Offers (SQL) */}
                    <Route path="/teklifler/verilen" element={<MainLayout><Offers /></MainLayout>} />
                    <Route path="/teklifler/verilen/yeni" element={<MainLayout><SalesInvoice mode="offer" /></MainLayout>} />
                    <Route path="/teklifler/verilen/duzenle/:offerId" element={<MainLayout><SalesInvoice mode="offer" /></MainLayout>} />
                    <Route path="/teklifler/alinan/yeni" element={<MainLayout><PurchaseInvoice mode="offer" /></MainLayout>} />
                    <Route path="/teklifler/alinan/duzenle/:offerId" element={<MainLayout><PurchaseInvoice mode="offer" /></MainLayout>} />
                    <Route path="/teklifler/sablon-editor" element={<MainLayout><OfferTemplateEditor /></MainLayout>} />
                    
                    {/* ✅ İRSALİYE SAYFALARI - SalesInvoice mode='waybill' ile */}
                    <Route path="/irsaliyeler" element={<MainLayout><Waybills /></MainLayout>} />
                    <Route path="/irsaliye/yeni" element={<MainLayout><SalesInvoice mode="waybill" /></MainLayout>} />
                    <Route path="/irsaliye/duzenle/:invoiceId" element={<MainLayout><SalesInvoice mode="waybill" /></MainLayout>} />
                    
                    {/* ✅ SİPARİŞ SAYFALARI - SalesInvoice/PurchaseInvoice mode='order' ile */}
                    <Route path="/siparisler" element={<MainLayout><Orders /></MainLayout>} />
                    <Route path="/siparis/satis/yeni" element={<MainLayout><SalesInvoice mode="order" /></MainLayout>} />
                    <Route path="/siparis/satis/duzenle/:invoiceId" element={<MainLayout><SalesInvoice mode="order" /></MainLayout>} />
                    <Route path="/siparis/alis/yeni" element={<MainLayout><PurchaseInvoice mode="order" /></MainLayout>} />
                    <Route path="/siparis/alis/duzenle/:invoiceId" element={<MainLayout><PurchaseInvoice mode="order" /></MainLayout>} />
                    
                    {/* ✅ ÇEK/SENET SAYFALARI */}
                    <Route path="/cek-senet" element={<MainLayout><ChecksNotes /></MainLayout>} />

                    {/* ✅ PROJE YÖNETİM SİSTEMİ (PYS) */}
                    <Route path="/projeler" element={<MainLayout><Projects /></MainLayout>} />
                    <Route path="/projeler/:id" element={<MainLayout><ProjectDetail /></MainLayout>} />
                    
                    <Route path="/currency" element={<MainLayout><Currency /></MainLayout>} />
                    
                    {/* Satış Sistemi Sayfaları */}
                    <Route path="/hizli-satis" element={<MainLayout><QuickSalePage /></MainLayout>} />
                    <Route path="/restoran" element={<MainLayout><RestaurantPage /></MainLayout>} />
                    <Route path="/siparis-yonetimi" element={<MainLayout><OrderManagementPage /></MainLayout>} /> {/* ✅ Sipariş Yönetim Sistemi (Admin) */}
                    <Route path="/tum-siparisler" element={<MainLayout><AllOrdersPage /></MainLayout>} />
                    <Route path="/restaurant-orders" element={<MainLayout><RestaurantOrdersReport /></MainLayout>} />
                    <Route path="/gun-sonu" element={<MainLayout><EndOfDayReport /></MainLayout>} />
                    <Route path="/gun-sonlari" element={<MainLayout><EndOfDayReportsList /></MainLayout>} />
                    <Route path="/qr-menu-management" element={<MainLayout><QRMenuPage /></MainLayout>} />
                    <Route path="/qr-menu/kitchen" element={<MainLayout><KitchenMenuSelection /></MainLayout>} />
                    <Route path="/mutfak-ekrani-secimi" element={<MainLayout><KitchenMenuSelection /></MainLayout>} />
                    <Route path="/qr-menu/customer-display" element={<MainLayout><PoleDisplayMenuSelection /></MainLayout>} />
                    <Route path="/musteri-ekrani-yonetimi" element={<MainLayout><PoleDisplayManagement /></MainLayout>} />
                    <Route path="/qr-menu/orders" element={<MainLayout><OrdersPage /></MainLayout>} />
                    <Route path="/qr-menu/customers" element={<MainLayout><CustomersPage /></MainLayout>} />
                    <Route path="/qr-menu/tables" element={<MainLayout><TablesPage /></MainLayout>} />
                    <Route path="/kiosk-menu" element={<MainLayout><KioskMenuPage /></MainLayout>} />
                    <Route path="/kiosk-products" element={<MainLayout><KioskProducts /></MainLayout>} />
                    <Route path="/kulup-giris" element={<MainLayout><ClubEntryPage /></MainLayout>} />
                    <Route path="/e-commerce" element={<MainLayout><ECommercePage /></MainLayout>} />
                    <Route path="/kurye" element={<MainLayout><CourierManagement /></MainLayout>} />
                    <Route path="/entegrasyonlar" element={<MainLayout><IntegrationPage /></MainLayout>} />
                    <Route path="/hacioglu-urun-eslestirme" element={<MainLayout><HaciogluProductMapping /></MainLayout>} />
                    <Route path="/cihaz-yonetimi" element={<MainLayout><DeviceManagement /></MainLayout>} />
                    <Route path="/kart-yonetimi/kartlar" element={<MainLayout><Cards /></MainLayout>} />
                    <Route path="/kart-yonetimi/oturumlar" element={<MainLayout><CardSessions /></MainLayout>} />
                    <Route path="/kart-yonetimi/silinen-kartlar" element={<MainLayout><DeletedCards /></MainLayout>} />
                    <Route path="/quick-sale-screen" element={<QuickSaleScreen />} />
                    <Route path="/pyk-screen" element={<PykScreenView />} />
                    
                    {/* QR Menü Müşteri Görünümü - Anasayfa dışında gösteriliyor */}
                    {/* <Route path="/qr-menu/:menuId" element={<QRMenuCustomerView />} /> */}

                    {/* İnsan Kaynakları Sayfaları */}
                    <Route path="/ik/izin" element={<MainLayout><LeaveManagementPage /></MainLayout>} />
                    <Route path="/ik/performans" element={<MainLayout><PerformanceManagementPage /></MainLayout>} />
                    <Route path="/ik/egitim" element={<MainLayout><TrainingDevelopmentPage /></MainLayout>} />
                    <Route path="/ik/bordro" element={<MainLayout><PayrollPaymentsPage /></MainLayout>} />
                    <Route path="/ik/dokuman" element={<MainLayout><DocumentManagementPage /></MainLayout>} />
                    <Route path="/ik/isealim" element={<MainLayout><RecruitmentManagementPage /></MainLayout>} />

                    {/* Muhasebe Yolları */}
                    <Route path="/accounting" element={<MainLayout><AccountingDashboard /></MainLayout>} />
                    <Route path="/accounts" element={<MainLayout><Accounts /></MainLayout>} />
                    <Route path="/inventory" element={<MainLayout><Inventory /></MainLayout>} />
                    <Route path="/bank" element={<MainLayout><Bank /></MainLayout>} />
                    <Route path="/credit-cards" element={<MainLayout><CreditCards /></MainLayout>} />
                    <Route path="/checkbooks" element={<MainLayout><CheckBooks /></MainLayout>} />
                    <Route path="/checkbooks/:id/leaves" element={<MainLayout><CheckBookLeaves /></MainLayout>} />
                    <Route path="/cashbox" element={<MainLayout><Cashbox /></MainLayout>} />
                    <Route path="/cashbox-transactions/:cashboxId" element={<MainLayout><CashboxTransactions /></MainLayout>} />
                    <Route path="/warehouse" element={<MainLayout><Warehouse /></MainLayout>} />
                    <Route path="/invoice" element={<MainLayout><Invoice /></MainLayout>} />
                    <Route path="/sales-invoice" element={<MainLayout><SalesInvoice /></MainLayout>} />
                    <Route path="/purchase-invoice" element={<MainLayout><PurchaseInvoice /></MainLayout>} />
                    <Route path="/offers" element={<MainLayout><Offers /></MainLayout>} />
                    {/* ✅ Eski route'lar (geriye dönük uyumluluk için) — SQL eşdeğerlerine yönlendirildi */}
                    <Route path="/given-offers" element={<MainLayout><Offers /></MainLayout>} />
                    <Route path="/given-offers/new" element={<MainLayout><SalesInvoice mode="offer" /></MainLayout>} />
                    <Route path="/given-offers/edit/:invoiceId" element={<MainLayout><SalesInvoice mode="offer" /></MainLayout>} />
                    <Route path="/received-offers/new" element={<MainLayout><PurchaseInvoice mode="offer" /></MainLayout>} />
                    <Route path="/received-offers/edit/:invoiceId" element={<MainLayout><PurchaseInvoice mode="offer" /></MainLayout>} />
                    <Route path="/currency" element={<MainLayout><Currency /></MainLayout>} />
                    <Route path="/stock-movements-report" element={<MainLayout><StockMovementsReport /></MainLayout>} />
                    <Route path="/account-movements-report" element={<MainLayout><AccountMovementsReport /></MainLayout>} />
                    <Route path="/account-transactions/:accountId" element={<MainLayout><AccountTransactions /></MainLayout>} />
                    <Route path="/bank-transaction-report" element={<MainLayout><BankTransactionReport /></MainLayout>} />

                    {/* Satış Sistemi Yolları (Admin) */}
                    <Route path="/quick-sale" element={<MainLayout><QuickSalePage /></MainLayout>} />
                    <Route path="/hizli-satis/:screenId" element={<QuickSaleScreen />} />
                    <Route path="/pyk-ekran/:screenId" element={<PykScreenView />} />
                    <Route path="/sales-screen-reports/:screenId" element={<MainLayout><SalesScreenReportsPage /></MainLayout>} />
                    <Route path="/restaurant-management" element={<MainLayout><RestaurantPage /></MainLayout>} />
                    <Route path="club-entry" element={<ClubEntryPage />} />

                    {/* WMS Yolları (Admin) */}
                    <Route path="/wms/panel" element={<MainLayout><WmsDashboard /></MainLayout>} />
                    <Route path="/wms/depo-tanimlama" element={<MainLayout><WmsWarehouseSetup /></MainLayout>} />
                    <Route path="/wms/depo-tanimlama/:warehouseId" element={<MainLayout><WmsLocationManager /></MainLayout>} />
                    <Route path="/wms/siparis-havuzu" element={<MainLayout><WmsOrderPool /></MainLayout>} />
                    <Route path="/wms/is-emirleri" element={<MainLayout><WmsWaveManagement /></MainLayout>} />
                    <Route path="/wms/toplama-ayarlari" element={<MainLayout><WmsPickingSetup /></MainLayout>} />
                    <Route path="/wms/iptal-yonetimi" element={<MainLayout><WmsCancelManagement /></MainLayout>} />
                    <Route path="/wms/paketleme-ayarlari" element={<MainLayout><WmsPackingSetup /></MainLayout>} />
                    <Route path="/wms/mal-kabul-ayarlari" element={<MainLayout><WmsReceivingSetup /></MainLayout>} />
                    <Route path="/wms/stok-sayim-ayarlari" element={<MainLayout><WmsCycleCountSetup /></MainLayout>} />
                    <Route path="/wms/mal-kabul-yonetimi" element={<MainLayout><WmsReceivingManagement /></MainLayout>} />
                    <Route path="/wms/stok-sayim-yonetimi" element={<MainLayout><WmsCycleCountManagement /></MainLayout>} />
                    <Route path="/wms/ekipman-yonetimi" element={<MainLayout><WmsEquipmentManagement /></MainLayout>} />
                    <Route path="/wms/raporlar" element={<MainLayout><WmsReports /></MainLayout>} />

                   </Route>
                  </Route>
                  
                  {/* Manager Routes */}
                  <Route element={<RoleRoute requiredRole="manager" />}>
                    <Route path="/manager" element={<ManagerLayout><ManagerPage /></ManagerLayout>} />
                    <Route path="/manager-profile" element={<ManagerLayout><ManagerProfilePage /></ManagerLayout>} />
                  </Route>
                  
                  {/* Employee & Courier Routes */}
                  <Route element={<RoleRoute requiredRole={["employee", "courier"]} />}>
                    <Route path="/personnel/tasks" element={<PersonnelTaskBoard />} />
                  </Route>
                </Route>
                
          {/* Fallback Route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </TableCartProvider>
      </TabProvider>
      </PublicRegistrationFlowProvider>
    </AuthProvider>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <CssBaseline />
      <HelmetProvider>
        <Router>
          <AppRoutes />
        </Router>
      </HelmetProvider>
    </ThemeProvider>
  );
};

export default App;
